import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IRD_FIELD_MAPPINGS } from '@shared/ird/fieldMapping.data';
import type { UserProfile } from '@shared/schema/userProfile';
import { getBill, markSubmitted, type BillRecord } from '@/lib/db/billsRepo';
import { getProfile } from '@/lib/db/profileRepo';
import CopyField from '@/features/ird/CopyField';
import CopyAutofillDataButton from '@/features/ird/CopyAutofillDataButton';
import SharePhotoButton from '@/features/ird/SharePhotoButton';

const IRD_URL = 'https://prize.ird.gov.np/';

type Step = 'safety' | 'assist';

export default function IrdAssistPage() {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const [bill, setBill] = useState<BillRecord | null | undefined>(undefined);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [step, setStep] = useState<Step>('safety');
  const [photoReady, setPhotoReady] = useState(false);
  const [willSolveCaptcha, setWillSolveCaptcha] = useState(false);
  const [willLeaveExtraFieldsBlank, setWillLeaveExtraFieldsBlank] = useState(false);
  const [marking, setMarking] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);

  useEffect(() => {
    if (!billId) return;
    void getBill(billId).then(setBill);
    void getProfile().then(setProfile);
  }, [billId]);

  if (bill === undefined) return null;
  if (bill === null) {
    return (
      <div className="p-4 pt-6">
        <p className="text-sm text-slate-500">This bill could not be found.</p>
      </div>
    );
  }

  const { billNumber, sellerPan, amount, billDateBs } = bill.billData;
  const allChecked = photoReady && willSolveCaptcha && willLeaveExtraFieldsBlank;

  function openIrdSite() {
    const opened = window.open(IRD_URL, '_blank', 'noopener');
    setStep('assist');
    // A blocked popup returns null (or, in some browsers, a closed window) —
    // surface that instead of silently doing nothing, since the cheat sheet
    // is useless if the user never realizes the real site never opened. Also
    // clears any earlier warning on a successful retry.
    setPopupBlocked(!opened || opened.closed);
  }

  async function handleMarkSubmitted() {
    if (!bill) return;
    setMarking(true);
    try {
      await markSubmitted(bill.id);
      navigate(`/history/${bill.id}`, { replace: true });
    } finally {
      setMarking(false);
    }
  }

  if (step === 'safety') {
    return (
      <div className="flex flex-col gap-6 p-4 pt-6">
        <header>
          <h1 className="text-xl font-bold text-slate-900">Ready to Submit</h1>
          <p className="mt-1 text-sm text-slate-500">
            This app never submits to IRD for you. You'll open the real IRD website and enter these values yourself.
          </p>
        </header>

        <dl className="grid grid-cols-2 gap-4 rounded-2xl bg-white p-4 text-sm shadow-sm">
          <Detail label="Bill Number" value={billNumber.value} />
          <Detail label="PAN" value={sellerPan.value} />
          <Detail label="Amount" value={amount.value !== null ? `Rs. ${amount.value.toFixed(2)}` : null} />
          <Detail label="Date (BS)" value={billDateBs.value} />
        </dl>

        <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Before you continue</h2>
          <Checkbox
            checked={photoReady}
            onChange={setPhotoReady}
            label="I have this bill's photo ready to attach (the real form requires a photo upload)."
          />
          <Checkbox
            checked={willSolveCaptcha}
            onChange={setWillSolveCaptcha}
            label="I understand IRD's site has a security check (CAPTCHA) that I must complete myself — this app cannot do it for me."
          />
          <Checkbox
            checked={willLeaveExtraFieldsBlank}
            onChange={setWillLeaveExtraFieldsBlank}
            label="I'll leave any field not listed here (like a 'company website' field) blank."
          />
        </div>

        <button
          type="button"
          onClick={openIrdSite}
          disabled={!allChecked}
          className="rounded-2xl bg-blue-700 px-6 py-5 text-lg font-semibold text-white shadow-sm active:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Open IRD Website
        </button>
        <button type="button" onClick={() => navigate(-1)} className="text-sm font-medium text-slate-500">
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 pt-6 pb-8">
      <header>
        <h1 className="text-xl font-bold text-slate-900">Fill In the IRD Form</h1>
        <p className="mt-1 text-sm text-slate-500">
          Switch to the IRD tab you just opened. Tap each value below to copy it, then paste it into the matching
          field on the real form, in this order.
        </p>
      </header>

      <button
        type="button"
        onClick={openIrdSite}
        className="rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800"
      >
        Reopen IRD Website ↗
      </button>

      {popupBlocked && (
        <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
          Your browser blocked the new tab. Please allow pop-ups for this site, or open{' '}
          <span className="font-semibold">{IRD_URL}</span> manually in another tab.
        </p>
      )}

      {bill.imageFull && <SharePhotoButton photo={bill.imageFull} filename={`bill-${billNumber.value ?? bill.id}.jpg`} />}

      <div className="flex flex-col gap-2 rounded-2xl bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Fastest: Autofill</h2>
        <p className="text-xs text-slate-500">
          Set up the Autofill Bookmarklet once in Settings, then use it here: copy your data, switch tabs, and click
          the bookmarklet to fill the text fields and date automatically.
        </p>
        <CopyAutofillDataButton billData={bill.billData} profile={profile} />
      </div>

      <details className="rounded-2xl bg-white p-4 shadow-sm">
        <summary className="cursor-pointer text-sm font-semibold uppercase tracking-wide text-slate-500">
          Or copy fields manually
        </summary>
        <div className="mt-3 flex flex-col gap-2">
          {IRD_FIELD_MAPPINGS.map((mapping) => (
            <CopyField key={mapping.key} label={mapping.label} value={mapping.getValue(bill.billData, profile) ?? ''} />
          ))}
        </div>
      </details>

      <div role="note" className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
        Remember: leave any field not listed above (such as a hidden "company website" field) blank, and complete the
        security check yourself before submitting.
      </div>

      <div className="mt-2 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => void handleMarkSubmitted()}
          disabled={marking}
          className="rounded-2xl bg-emerald-700 px-6 py-4 text-base font-semibold text-white active:bg-emerald-800 disabled:opacity-60"
        >
          {marking ? 'Saving…' : "I've Submitted This Bill"}
        </button>
        <p className="text-center text-xs text-slate-400">
          This only updates your own history — we have no way to confirm submission with IRD.
        </p>
        <button type="button" onClick={() => setStep('safety')} className="text-sm font-medium text-slate-500">
          Back
        </button>
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-900">{value ?? '—'}</dd>
    </div>
  );
}

function Checkbox({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-start gap-3 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0"
      />
      <span>{label}</span>
    </label>
  );
}
